import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadGoogleMaps, getGoogleMapsApiKey } from '../../utils/googleMapsLoader';
import { formatNumber, getCurrentLocale } from '../../utils/propertyUtils';
import { useTravelPlaces } from '../../hooks/useTravelPlaces';
import { usePlacesAutocomplete, type PlaceResult } from '../../hooks/usePlacesAutocomplete';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/common/ToastProvider';

interface TravelTimeCardProps {
  latitude: number;
  longitude: number;
  className?: string;
}

// Hard cap on resolving a single place on RELOAD. Without this, a Google Maps
// auth/billing failure leaves `loadGoogleMaps` pending forever and the chip
// spins endlessly — so we fall back to "unavailable" after this elapses.
const RESOLVE_TIMEOUT_MS = 8000;

// resolvePlace outcomes (reload path only):
//   number        → resolved; this many estimated minutes by car.
//   'not-found'   → the geocoder ran but the saved name no longer resolves.
//   'unavailable' → couldn't reach the geocoder (no key / quota / offline).
type ResolveResult = number | 'not-found' | 'unavailable';

/** Haversine distance in meters. */
function distanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

/** meters → approximate city drive minutes at ~30 km/h. */
const driveMinutes = (meters: number) => Math.max(1, Math.ceil(meters / 500));

const norm = (name: string) => name.trim().toLowerCase();

/**
 * "كم يبعد عن وجهاتك اليومية؟" — the visitor adds their daily places (work,
 * kids' school) and sees an *approximate* driving time from this property.
 *
 * Validation: the place can ONLY be chosen from Google Places autocomplete — the
 * user picks a real, suggested place and we compute the time from its actual
 * coordinates. Free-typed gibberish produces no suggestions and therefore can't
 * be added (previously a fuzzy text-search would loosely match nonsense like
 * "sdfdhd" and fabricate a time). Names persist per visitor via
 * {@link useTravelPlaces} (account when signed in, browser storage otherwise);
 * on reload the saved name is re-geocoded for its time.
 *
 * Honesty note: this is NOT a routed drive time — it's straight-line (haversine)
 * distance at a flat ~30 km/h, shown with a "~" prefix.
 */
export default function TravelTimeCard({ latitude, longitude, className = '' }: TravelTimeCardProps) {
  const { t, i18n } = useTranslation(['property']);
  const locale = getCurrentLocale();
  const isRTL = i18n.dir() === 'rtl';

  const { user } = useAuth();
  const { showToast } = useToast();
  const { places, addPlace, removePlace } = useTravelPlaces();

  // Google Places autocomplete — the only way to enter a place.
  const {
    query,
    setQuery,
    suggestions,
    isLoading: isSearching,
    isReady,
    getPlaceDetails,
    clearSelection,
  } = usePlacesAutocomplete({ componentRestrictions: { country: 'sa' } });
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Keyed by normalized place name (stable across optimistic temp→server id swaps).
  const [minutesByName, setMinutesByName] = useState<Record<string, number | null>>({});

  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const serviceDivRef = useRef<HTMLDivElement | null>(null);
  // Names already resolved (or in flight), kept outside state so the resolver
  // effect doesn't cancel itself when it stores results.
  const requestedRef = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);
  const inputWrapRef = useRef<HTMLDivElement | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // New property → driving times are stale, recompute every chip.
  useEffect(() => {
    requestedRef.current = new Set();
    setMinutesByName({});
  }, [latitude, longitude]);

  // Close the suggestions dropdown on outside click.
  useEffect(() => {
    if (!showSuggestions) return;
    const onDown = (e: MouseEvent) => {
      if (inputWrapRef.current && !inputWrapRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showSuggestions]);

  // Lazily build a headless PlacesService (one hidden map, reused) for the
  // RELOAD path — recomputing saved places' times by name.
  const ensureService = useCallback(async (): Promise<google.maps.places.PlacesService | null> => {
    if (placesServiceRef.current) return placesServiceRef.current;
    if (!getGoogleMapsApiKey()) return null;
    try {
      await loadGoogleMaps();
      if (!serviceDivRef.current) {
        serviceDivRef.current = document.createElement('div');
        serviceDivRef.current.style.display = 'none';
        document.body.appendChild(serviceDivRef.current);
      }
      const tempMap = new google.maps.Map(serviceDivRef.current, {
        center: { lat: latitude, lng: longitude },
        zoom: 12,
      });
      placesServiceRef.current = new google.maps.places.PlacesService(tempMap);
      return placesServiceRef.current;
    } catch {
      return null;
    }
  }, [latitude, longitude]);

  // Reload-only: re-geocode a saved place NAME into an approximate drive time.
  const resolveSaved = useCallback((name: string): Promise<ResolveResult> => {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (r: ResolveResult) => { if (!settled) { settled = true; resolve(r); } };
      const timer = setTimeout(() => finish('unavailable'), RESOLVE_TIMEOUT_MS);
      ensureService()
        .then((service) => {
          if (!service) { clearTimeout(timer); finish('unavailable'); return; }
          service.textSearch(
            { query: name, location: new google.maps.LatLng(latitude, longitude), radius: 50000 },
            (results, status) => {
              clearTimeout(timer);
              const Status = google.maps.places.PlacesServiceStatus;
              const location = results?.[0]?.geometry?.location;
              if (status === Status.OK && location) {
                finish(driveMinutes(distanceMeters(latitude, longitude, location.lat(), location.lng())));
              } else if (status === Status.ZERO_RESULTS) {
                finish('not-found');
              } else {
                finish('unavailable');
              }
            },
          );
        })
        .catch(() => { clearTimeout(timer); finish('unavailable'); });
    });
  }, [ensureService, latitude, longitude]);

  // Resolve driving minutes for every saved place that doesn't have a value yet.
  useEffect(() => {
    const pending = places.filter((p) => !requestedRef.current.has(norm(p.name)));
    if (pending.length === 0) return;
    pending.forEach((p) => requestedRef.current.add(norm(p.name)));

    pending.forEach((place) => {
      const key = norm(place.name);
      resolveSaved(place.name).then((result) => {
        if (!mountedRef.current) return;
        setMinutesByName((prev) => ({ ...prev, [key]: typeof result === 'number' ? result : null }));
      });
    });
  }, [places, resolveSaved]);

  // Add a place the user PICKED from autocomplete — we already trust it's real,
  // so we use its exact coordinates (no fuzzy text match, no fabricated time).
  const addResolvedPlace = useCallback(async (picked: PlaceResult) => {
    if (isAdding) return;
    setIsAdding(true);
    try {
      const details = await getPlaceDetails(picked.placeId);
      if (!details || (!details.lat && !details.lng)) {
        showToast(t('property.travel.notFound'), 'error');
        return;
      }
      const minutes = driveMinutes(distanceMeters(latitude, longitude, details.lat, details.lng));
      const added = await addPlace(details.name);
      clearSelection();
      setShowSuggestions(false);
      if (!added) return; // empty or duplicate — nothing more to do
      const key = norm(details.name);
      requestedRef.current.add(key); // resolver effect shouldn't redo it
      setMinutesByName((prev) => ({ ...prev, [key]: minutes }));
      showToast(
        t(user ? 'property.travel.savedToAccount' : 'property.travel.savedLocally'),
        'success',
      );
    } finally {
      setIsAdding(false);
    }
  }, [isAdding, getPlaceDetails, latitude, longitude, addPlace, clearSelection, user, showToast, t]);

  // Save a name WITHOUT coordinates — only used when autocomplete is
  // unavailable (no Maps key / offline). The chip shows "—" (never a fabricated
  // time), so this can't mislead even if the text is junk.
  const addNameOnly = useCallback(async (name: string) => {
    if (isAdding) return;
    setIsAdding(true);
    try {
      const added = await addPlace(name);
      clearSelection();
      setShowSuggestions(false);
      if (!added) return;
      const key = norm(name);
      requestedRef.current.add(key);
      setMinutesByName((prev) => ({ ...prev, [key]: null }));
      showToast(
        t(user ? 'property.travel.savedToAccount' : 'property.travel.savedLocally'),
        'success',
      );
    } finally {
      setIsAdding(false);
    }
  }, [isAdding, addPlace, clearSelection, user, showToast, t]);

  // Enter / Add button:
  //  - a suggestion exists → add that real place (with its real time);
  //  - Maps is configured + autocomplete ready but found nothing → reject (it
  //    isn't a real place — this is what blocks gibberish);
  //  - Maps unavailable → save the name with no time ("—"), graceful fallback.
  const mapsConfigured = !!getGoogleMapsApiKey();
  const handleAdd = useCallback(async () => {
    if (suggestions.length > 0) {
      await addResolvedPlace(suggestions[0]);
      return;
    }
    const name = query.trim();
    if (!name) return;
    if (mapsConfigured && isReady) {
      showToast(t('property.travel.notFound'), 'error');
      return;
    }
    await addNameOnly(name);
  }, [suggestions, addResolvedPlace, query, mapsConfigured, isReady, addNameOnly, showToast, t]);

  return (
    // A block inside the property page's location section, so its title is an h3.
    <div className={className}>
      <h3 className="m-0 mb-1.5 text-[1.0625rem] font-semibold text-[var(--pv-color-text)]">{t('property.travel.title')}</h3>
      <p className="m-0 mb-4 text-[15px] leading-relaxed text-[var(--pv-color-text-muted)]">{t('property.travel.subtitle')}</p>

      {places.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2.5">
          {places.map((place) => {
            const minutes = minutesByName[norm(place.name)];
            return (
              <span
                key={place.id}
                className="inline-flex items-center gap-2.5 rounded-full border border-[var(--pv-color-accent-soft)] bg-[var(--pv-color-surface-sunken)] px-4 py-2"
              >
                <span className="inline-flex items-center gap-2 text-[15px] font-extrabold text-[var(--pv-color-text)]">
                  <i className="ri-car-line text-[22px] leading-none text-[#004D40]" aria-hidden></i>
                  {place.name}
                </span>
                <span className="text-[15px] font-extrabold text-[#004D40]">
                  {minutes === undefined
                    ? <i className="ri-loader-4-line animate-spin text-base" aria-hidden></i>
                    : minutes === null
                      ? <span aria-label={t('property.travel.unavailable')}>-</span>
                      : minutes >= 60
                        ? t('property.travel.byCarHours', {
                            hours: formatNumber(Math.floor(minutes / 60), locale),
                            minutes: formatNumber(minutes % 60, locale),
                          })
                        : t('property.travel.byCar', { minutes: formatNumber(minutes, locale) })}
                </span>
                <button
                  type="button"
                  onClick={() => void removePlace(place.id)}
                  className="-me-1 flex h-7 w-7 items-center justify-center rounded-full text-[var(--pv-color-text-muted)] transition hover:bg-[#FDEAEA] hover:text-[#B8171C]"
                  aria-label={t('property.travel.remove', { name: place.name })}
                >
                  <i className="ri-close-line text-xl leading-none" aria-hidden></i>
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-2.5 sm:flex-row">
        <div ref={inputWrapRef} className="relative flex-1">
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAdd(); } }}
            placeholder={t('property.travel.placeholder')}
            aria-autocomplete="list"
            aria-expanded={showSuggestions && suggestions.length > 0}
            className="w-full rounded-xl border border-[#E0E9E7] bg-[var(--pv-color-surface-soft)] px-4 py-3 text-[15px] text-[var(--pv-color-text)] outline-none transition focus:border-[#004D40] focus:bg-white disabled:cursor-not-allowed disabled:opacity-60"
          />
          {isSearching && (
            <i
              className={`ri-loader-4-line animate-spin absolute top-1/2 -translate-y-1/2 text-[#93A7A3] ${isRTL ? 'left-3' : 'right-3'}`}
              aria-hidden
            ></i>
          )}

          {/* Autocomplete suggestions — the only path to add a place */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              role="listbox"
              className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-[#E0E9E7] bg-white py-1 shadow-[0_12px_40px_-8px_rgba(0,77,64,0.18)]"
            >
              {suggestions.map((s) => (
                <button
                  key={s.placeId}
                  type="button"
                  role="option"
                  aria-selected={false}
                  // Keep focus on the input until the click lands.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void addResolvedPlace(s)}
                  className={`flex w-full items-start gap-2.5 px-3 py-2 transition hover:bg-[var(--pv-color-surface-sunken)] ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <i className="ri-map-pin-line mt-0.5 text-xl leading-none text-[#004D40]" aria-hidden></i>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold text-[var(--pv-color-text)]">{s.name}</span>
                    {s.address && <span className="block truncate text-[13.5px] text-[var(--pv-color-text-muted)]">{s.address}</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={isAdding}
          className="rounded-xl bg-[#004D40] px-6 py-3 text-[15px] font-extrabold text-white transition hover:bg-[#003930] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isAdding ? (
            <>
              <i className={`ri-loader-4-line animate-spin text-lg align-[-3px] ${isRTL ? 'ml-1' : 'mr-1'}`} aria-hidden></i>
              {t('property.travel.checking')}
            </>
          ) : (
            <>
              <i className={`ri-add-line text-xl align-[-4px] ${isRTL ? 'ml-1' : 'mr-1'}`} aria-hidden></i>
              {t('property.travel.add')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
